import { useState, useContext } from "react";
import { ThemeContext } from "../context/ThemeContext";
import API from "../services/api";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";

const SignupPage = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await API.post("/auth/signup", form);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden"
      style={{
        background: theme === "dark"
          ? "linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 30%, #16213e 60%, #0a0a1a 100%)"
          : "linear-gradient(135deg, #f0f0ff 0%, #e8e0f0 30%, #f0e8f8 60%, #e0e8ff 100%)"
      }}
    >
      <div className="absolute top-20 right-20 w-72 h-72 bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl animate-pulse-soft animation-delay-400" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md relative z-10"
      >
        <div className={`rounded-2xl p-8 sm:p-10 ${
          theme === "dark" ? "glass-card" : "glass-card-light"
        }`}>
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold gradient-text mb-2">LinkHub</h1>
            <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
              Create your account to get started.
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}>Name</label>
              <input
                type="text"
                name="name"
                placeholder="Your name"
                value={form.name}
                onChange={handleChange}
                required
                className={`input-base ${theme === "dark" ? "input-dark" : "input-light"}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}>Email</label>
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
                className={`input-base ${theme === "dark" ? "input-dark" : "input-light"}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}>Password</label>
              <input
                type="password"
                name="password"
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                required
                className={`input-base ${theme === "dark" ? "input-dark" : "input-light"}`}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating account...
                </span>
              ) : "Create Account"}
            </button>
          </form>

          <p className={`text-center text-sm mt-6 ${
            theme === "dark" ? "text-gray-400" : "text-gray-500"
          }`}>
            Already have an account?{" "}
            <Link to="/" className="text-violet-500 hover:text-violet-400 font-medium transition-colors">
              Sign In
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SignupPage;